import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { UnidadeConsumidora, Profile } from '../../types';
import { dispararAvisoProtocolo } from '../../src/lib/envioCancelamento';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';
import { supabase } from '../../src/lib/supabase';
import UCSearchPicker, { composeEnderecoUC } from './UCSearchPicker';
import {
    MotivoCancelamento, MOTIVO_LABEL, criarSolicitacao, buscarResponsavelPorNegociacao,
    STATUS_SOLICITACAO_LABEL, StatusSolicitacaoCancelamento,
} from '../../src/lib/cancelamentos';
import { propsFundoModal } from '../../src/lib/backdropModal';

interface NovaSolicitacaoCancelamentoModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: () => void;
}

const inputCls = "w-full border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:border-primary outline-none bg-white dark:bg-slate-900 dark:text-slate-100";
const labelCls = "block text-xs font-bold text-gray-500 dark:text-slate-400 mb-1 uppercase tracking-wider";

const fmtData = (v: string | null | undefined): string => {
    if (!v) return '—';
    const [ano, mes, dia] = String(v).slice(0, 10).split('-');
    return dia && mes && ano ? `${dia}/${mes}/${ano}` : String(v);
};

const hojeISO = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const NovaSolicitacaoCancelamentoModal: React.FC<NovaSolicitacaoCancelamentoModalProps> = ({ isOpen, onClose, onCreated }) => {
    const { addToast } = useToast();
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [vendedores, setVendedores] = useState<Profile[]>([]);

    const [uc, setUc] = useState<UnidadeConsumidora | null>(null);
    const [dataSolicitacao, setDataSolicitacao] = useState(hojeISO());
    const [motivoCategoria, setMotivoCategoria] = useState<MotivoCancelamento>('inadimplencia');
    const [motivoDetalhe, setMotivoDetalhe] = useState('');
    const [vendedorId, setVendedorId] = useState<string>('');
    const [prazoSaidaRateio, setPrazoSaidaRateio] = useState('');
    const [prazoUsoSaldo, setPrazoUsoSaldo] = useState('');
    const [buscandoVendedor, setBuscandoVendedor] = useState(false);
    /** Aviso (não bloqueio) de que a UC já tem cancelamento aberto ou concluído. */
    const [duplicidade, setDuplicidade] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        setUc(null);
        setDataSolicitacao(hojeISO());
        setMotivoCategoria('inadimplencia');
        setMotivoDetalhe('');
        setVendedorId('');
        setPrazoSaidaRateio('');
        setPrazoUsoSaldo('');
        setDuplicidade(null);

        supabase.from('profiles').select('id, full_name, role, status').eq('status', 'active').neq('role', 'consumidor')
            .order('full_name').then(({ data }) => setVendedores((data || []) as Profile[]));
    }, [isOpen]);

    const handleSelectUc = async (unidade: UnidadeConsumidora) => {
        setUc(unidade);
        setBuscandoVendedor(true);
        setDuplicidade(null);
        try {
            const [responsavelId] = await Promise.all([
                buscarResponsavelPorNegociacao(unidade.negociacao_id ?? null),
                // Duplicidade avisada JÁ na abertura: descobrir só na hora do
                // distrato significa que a solicitação repetida já entrou na lista
                // e alguém vai ter que apagar depois.
                (async () => {
                    const { data } = await supabase
                        .from('solicitacoes_cancelamento')
                        .select('id, status, data_solicitacao')
                        .eq('uc_id', unidade.id)
                        .in('status', ['aberta', 'em_andamento', 'concluida'])
                        .order('id', { ascending: false });
                    const anteriores = (data || []) as Array<{ id: number; status: string; data_solicitacao: string }>;
                    const emAberto = anteriores.find(s => s.status === 'aberta' || s.status === 'em_andamento');
                    const concluida = anteriores.find(s => s.status === 'concluida');
                    if (emAberto) {
                        setDuplicidade(`Esta UC já tem a solicitação #${emAberto.id} (${STATUS_SOLICITACAO_LABEL[emAberto.status as StatusSolicitacaoCancelamento]}) desde ${fmtData(emAberto.data_solicitacao)}. Abrir outra duplica o acompanhamento — confira se não é a mesma coisa.`);
                    } else if (concluida) {
                        setDuplicidade(`Esta UC já teve um cancelamento concluído (#${concluida.id}, de ${fmtData(concluida.data_solicitacao)}). Confirme se o pedido é mesmo de uma nova adesão antes de abrir.`);
                    }
                })(),
            ]);
            setVendedorId(responsavelId || '');
        } catch (e) {
            console.error('Erro ao preparar a solicitação de cancelamento', e);
        } finally {
            setBuscandoVendedor(false);
        }
    };

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!uc) {
            addToast('Selecione a Unidade Consumidora.', 'warning');
            return;
        }
        setLoading(true);
        try {
            const criada = await criarSolicitacao({
                uc,
                dataSolicitacao,
                motivoCategoria,
                motivoDetalhe,
                vendedorResponsavelId: vendedorId || null,
                prazoSaidaRateioEstimado: prazoSaidaRateio || null,
                prazoUsoSaldoEstimado: prazoUsoSaldo || null,
                createdBy: user?.id,
            });
            // Aviso de protocolo (#5830). Hoje não faz nada: a chave
            // `cancelamento_envio_modo` está 'desligado'. Nunca lança — a
            // solicitação já está gravada.
            dispararAvisoProtocolo({
                solicitacaoId: criada.id,
                clienteNome: uc.nome_cliente ?? null,
                protocolo: criada.protocolo ?? null,
                ucNumero: uc.numero_uc ?? null,
                dataSolicitacao,
                dataRetornoPrevista: criada.data_retorno_prevista ?? null,
            });
            // Protocolo e data de retorno vêm do trigger do banco. Se vierem vazios
            // (migration ainda não aplicada), o registro foi gravado mesmo assim.
            addToast(criada.protocolo
                ? `Solicitação registrada — protocolo ${criada.protocolo}${criada.data_retorno_prevista ? `, retorno previsto até ${fmtData(criada.data_retorno_prevista)}` : ''}.`
                : 'Solicitação de cancelamento registrada.', 'success');
            onCreated();
            onClose();
        } catch (error: any) {
            console.error('Erro ao criar solicitação de cancelamento', error);
            addToast('Erro ao registrar solicitação: ' + (error.message || 'Erro desconhecido'), 'error');
        } finally {
            setLoading(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in" {...propsFundoModal(() => onClose())}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-100 dark:border-slate-700/50 bg-gray-50/50 dark:bg-slate-800/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-500 rounded-lg border border-red-100 dark:border-red-800/50">
                            <AlertCircle size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 font-poppins">Nova Solicitação de Cancelamento</h2>
                            <p className="text-sm text-gray-500 dark:text-slate-400">Abre o acompanhamento; a geração do distrato acontece depois, a partir desta solicitação.</p>
                        </div>
                    </div>
                    <button onClick={onClose} aria-label="Fechar" className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <form id="nova-solicitacao-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6 space-y-5">
                    <div>
                        <label className={labelCls}>Unidade Consumidora *</label>
                        {uc ? (
                            <div className="flex items-center justify-between bg-gray-50 dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-2.5">
                                <div>
                                    <p className="text-sm font-semibold text-gray-800 dark:text-slate-100">{uc.nome_cliente} — UC {uc.numero_uc}</p>
                                    <p className="text-xs text-gray-500 dark:text-slate-400">{composeEnderecoUC(uc)}</p>
                                </div>
                                <button type="button" onClick={() => { setUc(null); setDuplicidade(null); }} className="text-xs font-semibold text-primary hover:opacity-80">Trocar</button>
                            </div>
                        ) : (
                            <UCSearchPicker
                                selecionadas={[]}
                                onAdd={handleSelectUc}
                                onRemove={() => {}}
                                max={1}
                                placeholder="Buscar UC por cliente, nº da UC ou CPF/CNPJ..."
                            />
                        )}
                        {duplicidade ? (
                            <div className="mt-2 flex items-start gap-2 rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50/70 dark:bg-amber-900/15 px-3 py-2.5">
                                <AlertCircle size={15} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                                <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">{duplicidade}</p>
                            </div>
                        ) : null}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className={labelCls}>Data da Solicitação *</label>
                            <input type="date" required value={dataSolicitacao} onChange={e => setDataSolicitacao(e.target.value)} className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls}>Vendedor Responsável {buscandoVendedor && <Loader2 size={11} className="inline animate-spin ml-1" />}</label>
                            <select value={vendedorId} onChange={e => setVendedorId(e.target.value)} className={inputCls}>
                                <option value="">Sem vendedor identificado</option>
                                {vendedores.map(v => <option key={v.id} value={v.id}>{v.full_name}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className={labelCls}>Motivo do Cancelamento *</label>
                            <select required value={motivoCategoria} onChange={e => setMotivoCategoria(e.target.value as MotivoCancelamento)} className={inputCls}>
                                {Object.entries(MOTIVO_LABEL).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className={labelCls}>Detalhes do Motivo</label>
                        <textarea value={motivoDetalhe} onChange={e => setMotivoDetalhe(e.target.value)} rows={2}
                            placeholder="Ex: cliente relatou insatisfação com o valor da fatura..." className={`${inputCls} resize-y`} />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className={labelCls}>Prazo Estimado — Saída do Rateio</label>
                            <input type="date" value={prazoSaidaRateio} onChange={e => setPrazoSaidaRateio(e.target.value)} className={inputCls} />
                            <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1">Estimativa manual — depende do protocolo com a distribuidora.</p>
                        </div>
                        <div>
                            <label className={labelCls}>Prazo Estimado — Uso do Saldo</label>
                            <input type="date" value={prazoUsoSaldo} onChange={e => setPrazoUsoSaldo(e.target.value)} className={inputCls} />
                        </div>
                    </div>
                </form>

                <div className="p-4 border-t border-gray-100 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-800/80 flex justify-end gap-3">
                    <button type="button" onClick={onClose} disabled={loading}
                        className="px-4 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors shadow-sm disabled:opacity-50">
                        Cancelar
                    </button>
                    <button type="submit" form="nova-solicitacao-form" disabled={loading || !uc}
                        className="px-6 py-2 bg-primary hover:opacity-90 text-white rounded-xl text-sm font-bold transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center gap-2 min-w-[160px]">
                        {loading ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                        {loading ? 'Salvando...' : 'Registrar Solicitação'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default NovaSolicitacaoCancelamentoModal;
